import ACTIONS from "./action";
import { patientTableColumns } from "../constant/patientTableColumns";
import {
  defaultFilters,
  defaultMultiples,
  defaultSavedListFilters,
  defaultSavedListSorting,
  THIS_YEAR
} from "../constant/filters";
import commonReducers from "@tailormed/common-client/store/reducer";
import { EditPatientsReducer } from "./EditPatientsReducer";
import { CALENDAR_WEEK_VIEW } from "../constant/viewTypes";
import { ViewType } from "../constant/general";
import { savedListColumns } from "../constant/savedList";
import { defaultSort } from "../utils/sort";

const initialSearchedPatients = { patients: [], total: 0 };

const defaultState = {
  settings: {},
  user: undefined,
  notificationPreferences: undefined,
  networkActions: {},
  patients: [],
  patientNotes: [],
  patientAttachments: [],
  patientTasks: [],
  patientCycles: [],
  filteredPatientCycles: [],
  units: [],
  assignees: [],
  users: [],
  userGroups: [],
  userGroupsWithUsers: [],
  environment: undefined,
  patientTableColumns,
  idpConfig: undefined,
  oktaClientId: undefined,
  tracking: undefined,
  filters: defaultFilters,
  searchedPatients: { ...initialSearchedPatients },
  searchedPatient: undefined,
  patientModal: { show: false, tab: 0 },
  idPatientModal: undefined,
  calendarViewType: CALENDAR_WEEK_VIEW,
  currentCycle: undefined,
  APSponsors: [],
  keepEditingCycleMode: false,
  searchText: "",
  currentPage: 1,
  searchResultCurrentPage: 1,
  sorting: defaultSort,
  enableFlash: false,
  viewType: ViewType.List.key,
  isLoadingEnabled: true,
  calendarOrderDateFilter: {},
  filterList: {},
  savedListFilters: {
    query: defaultSavedListFilters.query,
    sortingStatuses: defaultSavedListSorting,
    filterBy: defaultSavedListFilters.filterBy
  },
  tableData: { data: { data: [], total: 0 }, selectedPage: 1, sorter: {} },
  savedListColumns,
  formPopups: {
    visible: false
  },
  filterListStatuses: {
    id: undefined,
    isModified: false,
    selectedPage: 1,
    sorter: {},
    canModify: false,
    showDiscardModal: false
  },
  isSavedListinitialized: false,
  canViewOrDownloadPatientAttachments: null,
  multiples: defaultMultiples,
  csvPatientReportData: {
    data: [],
    isReadyToDownload: false
  },
  cycleAdminDateFilter: { fromDate: THIS_YEAR, toDate: null },
  isAddingPatientCycle: false,
  selectedOrderId: null,
  ...commonReducers.defaultState
};

const mergePatientWithOrderData = (patient, currentOrder) => ({
  ...patient,
  currentOrder: {
    ...patient.currentOrder,
    ...currentOrder,
    id: patient.currentOrder.id
  }
});

const handlerEditCurrentOrder = (patients, currentOrder) => {
  if (!patients || !patients.length) return patients;
  const patientIndex = patients.findIndex((p) => p.currentOrder.id === currentOrder.id);
  if (patientIndex > -1) {
    const newPatient = mergePatientWithOrderData(patients[patientIndex], currentOrder);

    return [...patients.slice(0, patientIndex), newPatient, ...patients.slice(patientIndex + 1)];
  }

  return patients;
};

const reducer = (state = defaultState, action) => {
  switch (action.type) {
    case ACTIONS.Types.SET_IDP_CONFIG: {
      const { idpConfig } = action;
      return { ...state, idpConfig };
    }

    case ACTIONS.Types.SET_USER: {
      const { user } = action;
      return { ...state, user };
    }

    case ACTIONS.Types.SET_NETWORK_ACTION: {
      const { actionType, isFetching, msg, code } = action;
      return { ...state, networkActions: { ...state.networkActions, [actionType]: { isFetching, msg, code } } };
    }

    case ACTIONS.Types.SET_ENVIRONMENT: {
      const { environment } = action;
      return { ...state, environment };
    }

    case ACTIONS.Types.SET_SETTINGS: {
      const { settings } = action;
      const settingsObject = {};
      settings.map((setting) => (settingsObject[setting.key] = setting));
      return { ...state, settings: settingsObject };
    }

    case ACTIONS.Types.SET_PATIENTS: {
      const { patients } = action;
      return { ...state, patients };
    }

    case ACTIONS.Types.SET_SEARCHED_PATIENTS: {
      const { patients, total, sorting } = action;

      // Sort by patient name and active status only if there is no sort set
      if (!sorting?.columnKey) {
        patients.sort((a, b) => {
          if (a.isActive > b.isActive) {
            return -1;
          } else if (a.isActive < b.isActive) {
            return 1;
          }
          return 0;
        });

        patients.sort((a, b) => {
          if (a.patient.name.toLowerCase() < b.patient.name.toLowerCase()) {
            return -1;
          } else if (a.patient.name.toLowerCase() > b.patient.name.toLowerCase()) {
            return 1;
          }
          return 0;
        });
      }
      return { ...state, searchedPatients: { patients, total } };
    }

    case ACTIONS.Types.CLEAR_SEARCHED_PATIENTS: {
      return { ...state, searchedPatients: { ...initialSearchedPatients } };
    }

    case ACTIONS.Types.SET_SEARCHED_PATIENT: {
      const { patient } = action;
      const searchedPatientMatch = state.searchedPatients.patients.find(
        (searchedPatient) => searchedPatient.id === patient.id
      );

      return {
        ...state,
        searchedPatient: {
          ...state.searchedPatient,
          ...patient,
          potentialSaving: {
            ...searchedPatientMatch?.potentialSaving,
            ...patient?.potentialSaving
          }
        }
      };
    }

    case ACTIONS.Types.SET_USER_GROUPS: {
      const { userGroups } = action;
      return { ...state, userGroups };
    }

    case ACTIONS.Types.SET_USER_GROUPS_WITH_USERS: {
      const { userGroupsWithUsers } = action;
      return { ...state, userGroupsWithUsers };
    }

    case ACTIONS.Types.SET_SHOW_PATIENT_MODAL: {
      const { show, tab } = action;
      return {
        ...state,
        patientModal: { show, tab },
        // Clear selected order when modal closes
        selectedOrderId: show ? state.selectedOrderId : null
      };
    }

    case ACTIONS.Types.SET_ID_PATIENT_MODAL: {
      const { idPatientModal } = action;
      return { ...state, idPatientModal };
    }

    case ACTIONS.Types.SET_CALENDAR_VIEW_TYPE: {
      const { calendarViewType } = action;
      return { ...state, calendarViewType };
    }

    case ACTIONS.Types.EDIT_PATIENTS: {
      return EditPatientsReducer(state, action);
    }

    case ACTIONS.Types.EDIT_CURRENT_ORDER: {
      let newState = state;
      const { currentOrder } = action;

      // Note: Need to handle all patients in store.
      newState = {
        ...newState,
        patients: handlerEditCurrentOrder(state.patients, currentOrder),
        searchedPatients: {
          ...newState.searchedPatients,
          patients: handlerEditCurrentOrder(state.searchedPatients?.patients, currentOrder)
        }
      };

      const currSearchedPatient = state.searchedPatient;
      if (currSearchedPatient !== undefined) {
        const newPatient = mergePatientWithOrderData(currSearchedPatient, currentOrder);
        newState = { ...newState, searchedPatient: newPatient };
      }

      return newState;
    }

    case ACTIONS.Types.SET_PATIENT_CYCLES: {
      const { patientCycles, filteredPatientCycles } = action;
      return { ...state, patientCycles, filteredPatientCycles };
    }

    case ACTIONS.Types.ADD_PATIENT_CYCLE: {
      const { pharmacyPatient } = action;
      const currPatientCycles = state.patientCycles;
      const newPatientCycles = [...currPatientCycles, ...[pharmacyPatient]];
      return { ...state, patientCycles: newPatientCycles };
    }

    case ACTIONS.Types.DELETE_PATIENT_CYCLE: {
      const { id } = action;
      const index = state.patientCycles.findIndex((cycle) => cycle.id === id);
      if (index >= 0) {
        const currPatientCycles = state.patientCycles;
        const newPatientCycles = [...currPatientCycles.slice(0, index), ...currPatientCycles.slice(index + 1)];
        return { ...state, patientCycles: newPatientCycles };
      }
      return state;
    }

    case ACTIONS.Types.SET_PATIENT_NOTES: {
      const { notes, pharmacyPatient } = action;
      return {
        ...state,
        patientNotes: notes.filter(
          (patientNote) => !patientNote.pharmacyPatient || patientNote.pharmacyPatient === pharmacyPatient
        )
      };
    }

    case ACTIONS.Types.ADD_PATIENT_NOTES: {
      const { notes } = action;
      const currNotes = state.patientNotes;
      return { ...state, patientNotes: [...currNotes, ...notes] };
    }

    case ACTIONS.Types.UPDATE_PATIENT_NOTES: {
      const { notes } = action;
      notes.forEach((newNote) => {
        const index = state.patientNotes.findIndex((n) => n.id === newNote.id);
        if (index >= 0) {
          const currNote = state.patientNotes[index];
          const mergedNote = { ...currNote, ...newNote };
          state.patientNotes[index] = mergedNote;
          state = { ...state, patientNotes: [...state.patientNotes] };
        } else {
          state.patientNotes.push(newNote);
          state = { ...state, patientNotes: [...state.patientNotes] };
        }
      });
      return state;
    }

    case ACTIONS.Types.DELETE_PATIENT_NOTE: {
      const { noteId } = action;
      const index = state.patientNotes.findIndex((note) => note.id === noteId);
      if (index >= 0) {
        const currPatientNotes = state.patientNotes;
        const newPatientNotes = [...currPatientNotes.slice(0, index), ...currPatientNotes.slice(index + 1)];
        return { ...state, patientNotes: newPatientNotes };
      }
      return state;
    }

    case ACTIONS.Types.SET_PATIENT_ATTACHMENTS: {
      const { attachments, pharmacyPatient } = action;
      return {
        ...state,
        patientAttachments: attachments.filter(
          (patientAttachment) =>
            !patientAttachment.pharmacyPatient || patientAttachment.pharmacyPatient === pharmacyPatient
        )
      };
    }

    case ACTIONS.Types.DELETE_PATIENT_ATTACHMENT: {
      const { id } = action;
      const index = state.patientAttachments.findIndex((attachment) => attachment.id === id);
      if (index >= 0) {
        const currPatientAttachments = state.patientAttachments;
        const newPatientAttachments = [
          ...currPatientAttachments.slice(0, index),
          ...currPatientAttachments.slice(index + 1)
        ];
        return { ...state, patientAttachments: newPatientAttachments };
      }
      return state;
    }

    case ACTIONS.Types.SET_PATIENT_TASKS: {
      const { tasks } = action;
      return { ...state, patientTasks: tasks };
    }

    case ACTIONS.Types.ADD_PATIENT_TASKS: {
      const { tasks } = action;
      const currTasks = state.patientTasks;
      return { ...state, patientTasks: [...currTasks, ...tasks] };
    }

    case ACTIONS.Types.UPDATE_PATIENT_TASKS: {
      const { tasks } = action;
      tasks.forEach((newTask) => {
        const index = state.patientTasks.findIndex((t) => t.id === newTask.id);
        if (index >= 0) {
          const currTask = state.patientTasks[index];
          const mergedTask = { ...currTask, ...newTask };
          state.patientTasks[index] = mergedTask;
          state = { ...state, patientTasks: [...state.patientTasks] };
        } else {
          state.patientTasks.push(newTask);
          state = { ...state, patientTasks: [...state.patientTasks] };
        }
      });
      return state;
    }

    case ACTIONS.Types.DELETE_PATIENT_TASKS: {
      const { tasks } = action;
      tasks.forEach((deletedTask) => {
        const index = state.patientTasks.findIndex((task) => task.id === deletedTask.id);
        if (index >= 0) {
          const currPatientTasks = state.patientTasks;
          const newPatientTasks = [...currPatientTasks.slice(0, index), ...currPatientTasks.slice(index + 1)];
          state = { ...state, patientTasks: newPatientTasks };
        }
      });
      return state;
    }

    case ACTIONS.Types.CLEAR_PATIENT: {
      return {
        ...state,
        patientCycles: [],
        patientNotes: [],
        filteredPatientCycles: [],
        patientAttachments: [],
        searchedPatient: undefined,
        idPatientModal: undefined,
        currentCycle: undefined,
        enableFlash: false,
        selectedOrderId: null
      };
    }

    case ACTIONS.Types.SET_UNITES: {
      const { units } = action;
      return { ...state, units };
    }

    case ACTIONS.Types.SET_ASSIGNEES: {
      const { assignees } = action;
      return { ...state, assignees };
    }

    case ACTIONS.Types.SET_USERS: {
      const { users } = action;
      return { ...state, users };
    }

    case ACTIONS.Types.SET_PATIENT_TABLE_COLUMNS: {
      const { patientTableColumns } = action;
      return { ...state, patientTableColumns };
    }

    case ACTIONS.Types.SET_TRACKING: {
      const { tracking } = action;
      return { ...state, tracking };
    }

    case ACTIONS.Types.SET_FILTERS: {
      const { filters } = action;
      return { ...state, filters };
    }

    case ACTIONS.Types.SET_USER_EMAIL_CONFIGURATION: {
      const { user } = state;
      const { emailConfiguration } = action;

      return { ...state, user: { ...user, emailConfiguration } };
    }

    case ACTIONS.Types.SET_NOTIFICATION_PREFERENCES: {
      const { notificationPreferences } = action;
      return { ...state, notificationPreferences };
    }

    case ACTIONS.Types.SET_OKTA_CONFIG: {
      const { oktaClientId } = action;
      return { ...state, oktaClientId };
    }

    case ACTIONS.Types.SET_CURRENT_CYCLE: {
      const { currentCycle } = action;
      return { ...state, currentCycle };
    }

    case ACTIONS.Types.GET_AP_SPONSORS: {
      const { data } = action;
      return { ...state, APSponsors: data };
    }

    case ACTIONS.Types.SET_EDIT_CYCLE_MODE: {
      const { keepEditingCycleMode } = action;
      return { ...state, keepEditingCycleMode };
    }

    case ACTIONS.Types.SET_CAN_VIEW_OR_DOWNLOAD_PATIENT_ATTACHMENT: {
      const { canViewOrDownloadPatientAttachments } = action;
      return { ...state, canViewOrDownloadPatientAttachments };
    }

    case ACTIONS.Types.SET_CSV_PATIENT_REPORT_DATA: {
      const { csvPatientReportData } = action;
      return { ...state, csvPatientReportData };
    }

    case ACTIONS.Types.SET_TOTAL_PATIENTS: {
      const { totalPatients } = action;
      return { ...state, totalPatients };
    }

    case ACTIONS.Types.SET_CURRENT_PAGE: {
      const { currentPage } = action;
      return { ...state, currentPage };
    }

    case ACTIONS.Types.SET_SEARCH_RESULT_CURRENT_PAGE: {
      const { searchResultCurrentPage } = action;
      return { ...state, searchResultCurrentPage };
    }

    case ACTIONS.Types.SET_SEARCHED_TEXT: {
      const { searchText } = action;
      return { ...state, searchText };
    }

    case ACTIONS.Types.SET_SORTING: {
      const { sorting } = action;
      return { ...state, sorting };
    }

    case ACTIONS.Types.SET_ORDERS_TABLE_SEARCHED_TEXT: {
      const { ordersTableSearchText } = action;
      return { ...state, ordersTableSearchText };
    }

    case ACTIONS.Types.SET_CYCLE_FLASH_ENABLED: {
      const { enableFlash } = action;
      return { ...state, enableFlash };
    }

    case ACTIONS.Types.SET_VIEW_TYPE: {
      const { viewType } = action;
      return { ...state, viewType };
    }

    case ACTIONS.Types.SET_IS_LOADING_ENABLED: {
      const { isLoadingEnabled } = action;
      return { ...state, isLoadingEnabled };
    }

    case ACTIONS.Types.SET_IS_ADDING_PATIENT_CYCLE: {
      const { isAddingPatientCycle } = action;
      return { ...state, isAddingPatientCycle };
    }

    case ACTIONS.Types.SET_SELECTED_ORDER: {
      const { orderId } = action;
      return { ...state, selectedOrderId: orderId };
    }

    case ACTIONS.Types.CLEAR_SELECTED_ORDER: {
      return { ...state, selectedOrderId: null };
    }

    case ACTIONS.Types.SET_IS_FILTER_LIST_VISIABLE: {
      const { isVisiable } = action;
      return { ...state, isVisiable };
    }

    case ACTIONS.Types.SET_FILTER_LIST: {
      const { filterList } = action;
      return { ...state, filterList: filterList };
    }

    case ACTIONS.Types.SET_LIST_NAME_QUERY_ACTION: {
      const { query } = action;
      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          query
        }
      };
    }

    case ACTIONS.Types.SET_SAVED_LIST_SORT_BY_ACTION: {
      const { sortBy } = action;
      const { filterBy, sortingStatuses } = state.savedListFilters;
      const tabColumns = sortingStatuses[filterBy];
      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          sortingStatuses: {
            ...sortingStatuses,
            [filterBy]: { ...tabColumns, [tabColumns.orderBy]: sortBy }
          }
        }
      };
    }

    case ACTIONS.Types.SET_SAVED_LIST_ORDER_BY_ACTION: {
      const { orderBy } = action;
      const { filterBy, sortingStatuses } = state.savedListFilters;
      const tabColumns = sortingStatuses[filterBy];

      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          sortingStatuses: {
            ...sortingStatuses,
            [filterBy]: { ...tabColumns, orderBy }
          }
        }
      };
    }

    case ACTIONS.Types.SET_TABLE_DATA: {
      const { tableData, selectedPage, sorter } = action;

      return {
        ...state,
        tableData: { data: tableData, selectedPage, sorter }
      };
    }

    case ACTIONS.Types.SET_SAVED_LIST_TABLE_COLUMNS: {
      const { savedListColumns } = action;
      return { ...state, savedListColumns };
    }

    case ACTIONS.Types.SET_SAVED_LISTS_TABLE_RECORD_DATA: {
      const { recordData } = action;
      const savedListRecords = [...state.tableData.data.data];
      const listIndex = savedListRecords.findIndex((record) => record.id === recordData.id);
      savedListRecords[listIndex] = recordData;

      return {
        ...state,
        tableData: {
          ...state.tableData,
          data: {
            ...state.tableData.data,
            data: savedListRecords
          }
        }
      };
    }

    case ACTIONS.Types.SET_SAVED_TABLE_SELECTED_PAGE: {
      const { selectedPage } = action;
      return {
        ...state,
        tableData: {
          ...state.tableData,
          selectedPage: selectedPage
        }
      };
    }

    case ACTIONS.Types.SET_SELECTED_FILTER_LIST_TYPE: {
      const { listType } = action;
      return {
        ...state,
        savedListFilters: {
          ...state.savedListFilters,
          filterBy: listType
        }
      };
    }

    case ACTIONS.Types.SET_FORM_POPUPS: {
      const { visible, isDuplicateMode } = action;
      return { ...state, formPopups: { visible, isDuplicateMode } };
    }

    case ACTIONS.Types.UPDATE_FILTER_LIST_STATUSES: {
      const { filterListID, canModify, showDiscardModal } = action;
      return {
        ...state,
        filterListStatuses: {
          ...state.filterListStatuses,
          id: filterListID,
          canModify,
          showDiscardModal
        }
      };
    }

    case ACTIONS.Types.UPDATE_SAVED_FILTER_VIEW_PARAMETERS: {
      const { filterListID, selectedPage = 1, sorter = {} } = action;
      return {
        ...state,
        filterListStatuses: {
          ...state.filterListStatuses,
          id: filterListID,
          selectedPage,
          sorter
        }
      };
    }

    case ACTIONS.Types.MARK_SAVED_FILTER_AS_CHANGED: {
      const { filterListID, filterListStatus } = action;
      return {
        ...state,
        filterListStatuses: {
          ...state.filterListStatuses,
          id: filterListID,
          isModified: filterListStatus
        }
      };
    }

    case ACTIONS.Types.CLEAR_SORTER: {
      return {
        ...state,
        tableData: {
          ...state.tableData,
          sorter: {}
        }
      };
    }

    case ACTIONS.Types.CLEAR_FILTERS_INPUTS: {
      return { ...state, filters: defaultFilters };
    }

    case ACTIONS.Types.INIT_SAVED_LIST: {
      return { ...state, isSavedListinitialized: true };
    }

    case ACTIONS.Types.SET_SAVED_LIST_VIEW_TYPE: {
      const { savedListViewType } = action;
      return { ...state, savedListViewType };
    }

    case ACTIONS.Types.SET_CALENDAR_VIEW_TYPE_DATE: {
      const { calendarOrderDateFilter } = action;
      return { ...state, calendarOrderDateFilter };
    }

    case ACTIONS.Types.SET_MULTIPLES: {
      const { multiples } = action;
      return { ...state, multiples };
    }

    case ACTIONS.Types.SET_CYCLE_ADMIN_DATE_FILTER: {
      const { cycleAdminDateFilter } = action;
      return { ...state, cycleAdminDateFilter };
    }

    default: {
      return commonReducers.reducer(state, action);
    }
  }
};

export default reducer;
